import { type RefObject, useEffect, useRef, useState } from 'react';
import { easeInOut, lerp } from './layout';

export const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);

/** Tweens toward `target` over `ms` per full unit (eased); reduced motion snaps. */
export function useTween(target: number, ms: number): number {
  const [t, setT] = useState(target);
  const tRef = useRef(target);
  useEffect(() => {
    const from = tRef.current;
    if (from === target) return;
    if (prefersReducedMotion()) {
      tRef.current = target;
      setT(target);
      return;
    }
    const dur = Math.max(1, ms * Math.abs(target - from));
    const t0 = performance.now();
    let raf = 0;
    const step = (now: number) => {
      const p = Math.min(1, (now - t0) / dur);
      const v = lerp(from, target, p);
      tRef.current = v;
      setT(v);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);
  return easeInOut(t);
}

/** Content-box size of an element; a hidden/collapsed box (0) keeps the last good size. */
export function useSize(ref: RefObject<Element | null>): { w: number; h: number } {
  const [size, setSize] = useState({ w: 1000, h: 760 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      if (entry && entry.contentRect.width > 0 && entry.contentRect.height > 0)
        setSize({ w: entry.contentRect.width, h: entry.contentRect.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return size;
}
